import { create } from 'zustand'
import type { View, ViewSettings } from '../types'

export interface RoutePoint {
  x: number
  z: number
  floor: number
}

type FocusCameraFn = (x: number, y: number, z: number) => void

interface ViewState {
  // Visibility toggles
  zonesVisible: boolean
  wallsVisible: boolean
  floorplansVisible: boolean
  ifcWallsVisible: boolean
  pathSegmentsVisible: boolean
  occupantsVisible: boolean
  // Mode
  is2DMode: boolean
  liveMode: boolean
  routesMode: boolean
  // View state
  explodeValue: number
  selectedFloor: number | null
  isolatedFloor: number | null
  // Route (Phase 3)
  currentRoute: RoutePoint[] | null
  // Saved views
  views: View[]
  currentViewId: string | null
  // IFC rotation (real-time preview)
  ifcRotation: number
  // Camera focus bridge (Phase 1 - set by ThreeCanvas, called by SidePanel)
  focusCameraFn: FocusCameraFn | null
  // Actions - toggles
  toggleZones: () => void
  toggleWalls: () => void
  toggleFloorplans: () => void
  toggleIFCWalls: () => void
  togglePathSegments: () => void
  toggleOccupants: () => void
  toggle2DMode: () => void
  toggleLiveMode: () => void
  toggleRoutesMode: () => void
  setExplode: (v: number) => void
  selectFloor: (index: number | null) => void
  isolateFloor: (index: number | null) => void
  setIfcRotation: (r: number) => void
  setFocusCameraFn: (fn: FocusCameraFn | null) => void
  // Actions - route
  setCurrentRoute: (route: RoutePoint[]) => void
  clearRoute: () => void
  // Actions - views
  setViews: (views: View[]) => void
  loadView: (settings: ViewSettings) => void
  setCurrentViewId: (id: string | null) => void
  captureSettings: () => Omit<ViewSettings, 'cameraPosition' | 'cameraTarget' | 'cameraZoom'>
}

export const useViewStore = create<ViewState>((set, get) => ({
  zonesVisible: true,
  wallsVisible: true,
  floorplansVisible: true,
  ifcWallsVisible: true,
  pathSegmentsVisible: false,
  occupantsVisible: true,
  is2DMode: false,
  liveMode: true,
  routesMode: false,
  explodeValue: 0,
  selectedFloor: null,
  isolatedFloor: null,
  currentRoute: null,
  views: [],
  currentViewId: null,
  ifcRotation: 0,
  focusCameraFn: null,
  toggleZones: () => set((s) => ({ zonesVisible: !s.zonesVisible })),
  toggleWalls: () => set((s) => ({ wallsVisible: !s.wallsVisible })),
  toggleFloorplans: () => set((s) => ({ floorplansVisible: !s.floorplansVisible })),
  toggleIFCWalls: () => set((s) => ({ ifcWallsVisible: !s.ifcWallsVisible })),
  togglePathSegments: () => set((s) => ({ pathSegmentsVisible: !s.pathSegmentsVisible })),
  toggleOccupants: () => set((s) => ({ occupantsVisible: !s.occupantsVisible })),
  toggle2DMode: () => set((s) => ({ is2DMode: !s.is2DMode })),
  toggleLiveMode: () => set((s) => ({ liveMode: !s.liveMode })),
  toggleRoutesMode: () => set((s) => ({ routesMode: !s.routesMode })),
  setExplode: (v) => set({ explodeValue: v }),
  selectFloor: (index) => set({ selectedFloor: index }),
  isolateFloor: (index) => set((s) => ({ isolatedFloor: s.isolatedFloor === index ? null : index })),
  setIfcRotation: (r) => set({ ifcRotation: r }),
  setFocusCameraFn: (fn) => set({ focusCameraFn: fn }),
  setCurrentRoute: (route) => set({ currentRoute: route }),
  clearRoute: () => set({ currentRoute: null }),
  setViews: (views) => set({ views }),
  loadView: (settings) => set({
    zonesVisible: settings.zonesVisible,
    wallsVisible: settings.wallsVisible,
    floorplansVisible: settings.floorplansVisible ?? true,
    ifcWallsVisible: settings.ifcWallsVisible ?? true,
    pathSegmentsVisible: settings.pathSegmentsVisible ?? false,
    occupantsVisible: settings.occupantsVisible,
    explodeValue: settings.explodeValue,
    isolatedFloor: settings.isolatedFloor,
    is2DMode: settings.is2DMode ?? false,
  }),
  setCurrentViewId: (id) => set({ currentViewId: id }),
  captureSettings: () => {
    const s = get()
    return {
      zonesVisible: s.zonesVisible,
      wallsVisible: s.wallsVisible,
      floorplansVisible: s.floorplansVisible,
      ifcWallsVisible: s.ifcWallsVisible,
      pathSegmentsVisible: s.pathSegmentsVisible,
      occupantsVisible: s.occupantsVisible,
      explodeValue: s.explodeValue,
      isolatedFloor: s.isolatedFloor,
      is2DMode: s.is2DMode,
    }
  },
}))
